import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Loader2, PanelRightOpen } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { APIProvider } from "@vis.gl/react-google-maps";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import {
  usePreFinalizeCustomQuotation,
  usePreFinalizeCustomQuotationFedex,
  usePreFinalizeCustomQuotationSimplified,
} from "@/api/expedition.api";
import { toast } from "react-toastify";
import { useGetAllProducts } from "@/api/product.api";
import { useGetAllProductsUsaByCompany } from "@/api/productUsa.api";
import {
  preFinalizeCustomQuotationFedexPayload,
  preFinalizeCustomQuotationPayload,
  CustomQuotationUpdateBoDefaultValues,
  CUSTOM_QUOTATION_VIA_IMPORTED_INVOICE,
  preFinalizeCustomQuotationSimplifiedPayload,
} from "../expeditionFormHelper";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  CustomQuotationUpdateBoFormData,
  CustomQuotationUpdateBoFormSchema,
} from "@/types/forms/bo/expeditionBo.form";
import { ExpeditionFormStepProps } from "../expeditionForm.types";
import { ExpeditionHeader } from "../expeditionHeader";
import { ApiInfo } from "../shared/sections/apiInfo/apiInfo";
import { DeliveryContent } from "../shared/sections/deliveryContent/deliveryContent";
import { ExpeditionDeliveryInfo } from "../shared/sections/deliveryInfo/deliveryInfo";
import { SideContent, SideContentMobileModal } from "../shared/sections/sidePanel/sideContent";
import { useGetCustomV1Bo } from "@/api/customs.api";
import { FinalizeApiEnum } from "@/types/finalizeApi.type";

export const CustomQuotationExpeditionFormStepOne = ({
  expedition,
  company,
  apiEnum,
  formStep,
  setFormStep,
  onApiEnumConfirmed,
}: ExpeditionFormStepProps) => {
  const { t } = useTranslation("boExpeditionForm");
  const methods = useForm<CustomQuotationUpdateBoFormData>({
    resolver: zodResolver(CustomQuotationUpdateBoFormSchema),
    defaultValues: CustomQuotationUpdateBoDefaultValues({ expedition, company, apiEnum }),
  });
  const { mutateAsync: preFinalizeCustomQuotation, isPending: isPreFinalizingCustomQuotation } =
    usePreFinalizeCustomQuotation();
  const {
    mutateAsync: preFinalizeCustomQuotationFedex,
    isPending: isPreFinalizingCustomQuotationFedex,
  } = usePreFinalizeCustomQuotationFedex();
  const {
    mutateAsync: preFinalizeCustomQuotationSimplified,
    isPending: isPreFinalizingCustomQuotationSimplified,
  } = usePreFinalizeCustomQuotationSimplified();
  const {
    data: custom,
    isLoading: isLoadingCustoms,
    isError: isErrorCustoms,
  } = useGetCustomV1Bo(expedition.customQuotation?.customId ?? "");

  const isPending =
    isPreFinalizingCustomQuotation ||
    isPreFinalizingCustomQuotationFedex ||
    isPreFinalizingCustomQuotationSimplified;

  const [isSideContentOpen, setIsSideContentOpen] = useState(false);

  const importerId = useWatch({ control: methods.control, name: "importerId" });
  const countryCode = useWatch({ control: methods.control, name: "deliveryInfo.countryCode" });
  const apiEnumWatch = useWatch({ control: methods.control, name: "apiEnum" });

  const {
    data: products,
    isLoading: isLoadingProducts,
    isError: isErrorProducts,
  } = useGetAllProducts();
  const {
    data: productsUSA,
    isLoading: isLoadingProductsUSA,
    isError: isErrorProductsUSA,
  } = useGetAllProductsUsaByCompany(company.id, importerId);

  if (isLoadingProducts || isLoadingProductsUSA || isLoadingCustoms) {
    return <Loader2 className="animate-spin" />;
  }

  if (isErrorProducts || isErrorProductsUSA || isErrorCustoms) {
    return <div>{t("formStepOne.loadError")}</div>;
  }

  const isUsaProducts = countryCode === "US";
  const productsList = isUsaProducts ? productsUSA : products?.data;

  const onSubmit = async (data: CustomQuotationUpdateBoFormData) => {
    if (expedition.notification?.some((n) => n.readBy == null)) {
      toast.error(t("formStepOne.unreadNotifications"), { position: "top-center" });
      return;
    }
    if (data.apiEnum === "FEDEX") {
      const payload = preFinalizeCustomQuotationFedexPayload({
        data: data as Extract<CustomQuotationUpdateBoFormData, { apiEnum: "FEDEX" }>,
      });

      await preFinalizeCustomQuotationFedex({
        id: expedition.id,
        payload: payload,
      });
    } else if (data.apiEnum === "NONE") {
      const payload = preFinalizeCustomQuotationPayload({
        data: data as Extract<CustomQuotationUpdateBoFormData, { apiEnum: "NONE" }>,
      });

      await preFinalizeCustomQuotation({
        id: expedition.id,
        payload: payload,
      });
    } else {
      const payload = preFinalizeCustomQuotationSimplifiedPayload({
        data: data as Extract<CustomQuotationUpdateBoFormData, { apiEnum: "NONE_SIMPLIFIED" }>,
      });

      await preFinalizeCustomQuotationSimplified({
        id: expedition.id,
        payload: payload,
      });
    }

    onApiEnumConfirmed?.(data.apiEnum);
    setFormStep(2);
  };

  return (
    <APIProvider apiKey="AIzaSyDIEG5sck092TlO8WTxyEQjawmwtPMf3vw">
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit(onSubmit)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
            }
          }}
          className="@container absolute inset-0 flex flex-col overflow-hidden"
        >
          <ExpeditionHeader expedition={expedition} formStep={formStep} setStep={setFormStep} />
          <div className="relative flex w-full flex-col justify-between gap-2 overflow-hidden @min-[896px]:flex-row">
            <Accordion
              type="multiple"
              className="@container flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4"
              defaultValue={["deliveryInfo", "deliveryContent", "apiInfo", "pickupAndBilling"]}
            >
              <ExpeditionDeliveryInfo expedition={expedition} custom={custom} />
              {apiEnumWatch !== FinalizeApiEnum.NONE_SIMPLIFIED && (
                <>
                  <DeliveryContent
                    products={productsList}
                    isUsaProducts={isUsaProducts}
                    companyId={company.id}
                    countryCode={countryCode}
                    viaImportedInvoice={CUSTOM_QUOTATION_VIA_IMPORTED_INVOICE}
                  />
                  <ApiInfo expedition={expedition} company={company} />
                </>
              )}
              <Button type="submit" className="w-fit self-start" disabled={isPending}>
                {isPending ? <Loader2 className="animate-spin" /> : t("formStepOne.submit")}
              </Button>
            </Accordion>
            <div className="hidden min-h-0 w-[440px] flex-col gap-4 overflow-y-auto p-4 @min-[896px]:flex">
              <SideContent expedition={expedition} company={company} />
            </div>
            <Button
              type="button"
              variant="default"
              onClick={() => setIsSideContentOpen(true)}
              aria-label={t("formStepOne.openDetails")}
              className="absolute right-4 bottom-4 z-30 flex h-12 w-12 items-center justify-center rounded-full shadow-lg @min-[896px]:hidden [&_svg]:h-6 [&_svg]:w-6"
            >
              <PanelRightOpen />
            </Button>
            <SideContentMobileModal
              open={isSideContentOpen}
              onClose={() => setIsSideContentOpen(false)}
              expedition={expedition}
              company={company}
            />
          </div>
        </form>
      </FormProvider>
    </APIProvider>
  );
};
